<template>
  <v-card class="pa-0" elevation="0">
    <CheckoutTimer v-if="disabled"/>

    <v-card-text class="px-8 py-10 px-md-10" v-else>
      <div class="d-flex mb-8 black--text">
        <h2>Provide delivery details</h2>
      </div>
      <ValidationObserver ref="observer">
        <form>
          <v-card class="px-6 py-2" outlined>
            <div class="body-1 text-left pa-0 grey--text text--darken-3 mt-3 mb-7">Contact details</div>
            <ValidationProvider name="full_name" rules="required" v-slot="{ errors }">
              <v-text-field
                :disabled="loading"
                :error-messages="errors"
                dense
                label="Name"
                v-model="values.full_name"
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider name="email" rules="required|email" v-slot="{ errors }">
              <v-text-field
                :disabled="loading"
                :error-messages="errors"
                dense
                label="Email address"
                v-model="values.email"
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider name="phone_no" rules="required|phone" v-slot="{ errors }">
              <v-text-field
                :disabled="loading"
                :error-messages="errors"
                dense
                label="Phone number"
                v-model="values.phone_no"
              ></v-text-field>
            </ValidationProvider>
          </v-card>
          <v-card class="px-6 py-2 mt-5" outlined>
            <div class="body-1 text-left pa-0 grey--text text--darken-3 mt-3 mb-2">Home address</div>
            <p class="mb-7">This address will be used for delivery</p>
            <ValidationProvider name="address" rules="required|address" v-slot="{ errors }">
              <v-textarea
                :disabled="loading"
                :error-messages="errors"
                dense
                label="Address"
                placeholder="format: House No, Street, LGA"
                rows="2"
                v-model="values.address"
              ></v-textarea>
            </ValidationProvider>
            <ValidationProvider name="state" rules="required" v-slot="{ errors }">
              <v-select
                :disabled="loading"
                :error-messages="errors"
                :items="states"
                :loading="!states.length"
                dense
                label="State"
                v-model="values.state"
              ></v-select>
            </ValidationProvider>
          </v-card>
        </form>
        <v-btn :loading="loading" @click="validate" block class="mt-10" color="primary" elevation="0" large>
          {{ (repayment.monthly && $store.state.merchant.partnership === 1) ? 'Get funded' : 'Send request' }}
        </v-btn>
        <v-btn :disabled="loading" @click="$emit('cancel')" block class="mt-2" color="red" large text>Cancel</v-btn>
      </ValidationObserver>

      <v-snackbar color="red" top v-model="error">Please upload a profile picture</v-snackbar>
    </v-card-text>
  </v-card>
</template>

<script>
import states from "@/lib/states";
import CheckoutTimer from "./CheckoutTimer";

export default {
  name: "Checkout",
  components: { CheckoutTimer },
  props: {
    checkout: {
      type: Boolean,
      default: true
    },
    lender: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: false,
      states,
      lgas: [],
      values: {
        full_name: '',
        email: '',
        phone_no: '',
        address: '',
        state: null
      },
      disabled: false
    }
  },

  created() {
    const { phone, ...personal } = this.$store.state.customer.personal;
    this.values = { ...personal, phone_no: phone }
  },

  computed: {
    credit() {
      return !!this.$store.getters['cart/repayment'].monthly;
    },
    repayment() {
      return this.$store.getters["cart/repayment"]
    },
  },

  methods: {
    async validate() {
      const valid = await this.$refs.observer.validate();
      if (valid) {
        if (this.checkout) await this.submit()
        else this.$emit('done', this.values);
      }
    },

    async submit() {
      this.loading = true;
      const id = await this.createRequest();
      if (id) {
        if (this.credit) {
          await this.updateSchedule(id);
          this.sendVerificationEmail(id);
        }
        this.generateLink(id);
        await this.finish();
      }
      this.loading = false
    },

    async finish() {
      const { slug } = this.$store.state.request;
      this.$store.commit('request/clear');
      this.$store.commit('cart/clear');
      await this.$router.push(`/request/${ slug }`);
      this.$emit('done');
    },

    async createRequest() {
      const { merchant } = this.$store.state;
      const { id: catalogue_id } = this.$store.state.catalogue;
      const { items } = this.$store.state.cart;
      const upfront_amt = this.$store.getters['cart/payableAmount'];
      const total_credit = this.$store.getters['cart/repayment'].total;
      const no_of_months = this.$store.getters['cart/repayment'].months;
      const monthly_amt = this.$store.getters['cart/repayment'].monthly;
      try {
        const res = await this.$http.post('https://sellbackend.creditclan.com/merchantclanbackend/public/index.php/api/request', {
          merchant_id: merchant ? merchant.id : null,
          catalogue_id,
          upfront_amt,
          total_credit,
          cred_plus_upfront: upfront_amt + total_credit,
          no_of_months,
          monthly_amt,
          item_id: items.map(item => item.id).join(', '),
          items: JSON.stringify([...items.map(item => ({ id: item.id, ...item.plan, }))]),
          lender: this.lender,
          ...this.values
        });
        const { customer, ...request } = res.data.data;
        this.$store.commit('request/clear');
        this.$store.commit('request/set', request);
        this.$store.commit('customer/prefill', customer);
        return request.id;
      } catch (e) {
        console.log('Request error..', { e });
        return false
      }
    },

    async sendVerificationEmail(id) {
      await this.$http.post('https://sellbackend.creditclan.com/merchantclan/public/index.php/api/send/checkout/email', {
        request_id: id.toString()
      });
    },

    async generateLink(id) {
      try {
        await this.$http.patch(`https://sellbackend.creditclan.com/merchantclanbackend/public/index.php/api/request/${ id }/generate`, {
          url: `https://shop.creditclan.com/request/${ id }`
        });
      } catch (e) {
        return null
      }
    },

    async updateSchedule(id) {
      const payload = this.$store.getters['cart/schedule'];
      await this.$http.post(`https://sellbackend.creditclan.com/merchantclanbackend/public/index.php/api/request_schedule/${ id }`, { payload })
    },
  },
}
</script>
